// styles/PackageIndexStyles.js
import styled, { css } from 'styled-components';

// === COLOR TOKENS (extendable for theming/dark mode) ===
export const colors = {
  background: 'var(--white-a15)',
  backdrop: 'blur(18px)',
  border: 'var(--white-a66)',
  green: 'var(--green-800)',
  greenAlt: 'var(--green-800)',
  orange: 'var(--orange-500)',
  orangeLight: 'var(--orange-50)',
  white: 'var(--white)',
  text: 'var(--grey-700)',
  shadow: 'var(--shadow-a08)',
  shadowHeavy: 'var(--shadow-a15)',
  overlay: 'var(--shadow-a55)',
  glassWhite: 'var(--white-a40)',
};

// === SHARED UTILITIES ===
export const sharedScrollStyle = css`
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${colors.shadowHeavy};
    border-radius: 4px;
    border: 2px solid ${colors.border};
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  scrollbar-width: thin;
  scrollbar-color: ${colors.shadowHeavy} transparent;
`;

// === PAGE STRUCTURE ===
export const PageWrapper = styled.div`
  display: flex;
  max-height: 100%;
  overflow: hidden;
  background: ${colors.background};
  backdrop-filter: ${colors.backdrop};
  border-radius: 20px;
  border: 1px solid ${colors.border};
  box-shadow: 0 8px 32px ${colors.shadow};
  box-sizing: border-box;
`;

export const VirtualizedGridWrapper = styled.div`
  flex: 1;
  position: relative;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  overflow: hidden;
`;

export const FiltersContainer = styled.div`
  width: 250px;
  padding: 1rem;
  border-radius: 16px 0 0 16px;
  background: ${colors.background};
  backdrop-filter: blur(16px);
  border-right: 1px solid var(--white-a15);
  border-top: 1px solid var(--white-a15);
  border-bottom: 1px solid var(--white-a15);
  box-shadow: 2px 0 10px ${colors.shadow};
  transition: all 0.3s ease;
  overflow-y: auto;
  max-height: 100vh;
  overscroll-behavior: contain;
  ${sharedScrollStyle}
`;


export const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 1rem;
  height: 93vh;
  Width: 100vh;
  box-sizing: border-box;
`;

export const ScrollableContent = styled.div`
  flex: 1;
  z-index: 1;
  overflow-y: auto;
  overflow-x: auto;
  scroll-behavior: smooth;
  overscroll-behavior: contain;
  min-height: 0;
  ${sharedScrollStyle}
`;

export const FormPanel = styled.div`
  background: linear-gradient(to bottom, var(--cream-300), var(--cream-200));
  backdrop-filter: ${colors.backdrop};
  border-radius: 20px;
  padding: 1rem;
  width: 95%;
  max-width: 95%;
  max-height: 80%;
  color: var(--black);
  position: fixed;
  overflow-y: auto;
  overscroll-behavior: contain;
  box-shadow: 0 8px 24px ${colors.shadowHeavy};
  ${sharedScrollStyle}

  /* Always horizontally centered */
  left: 50%;
  transform: translateX(-50%);
  
  @media (max-width: 768px) {
    width: 95%;
    padding: 1.25rem;
  }
`;

export const Panel = styled.div`
  background: linear-gradient(to bottom, var(--cream-300), var(--cream-200));
  backdrop-filter: ${colors.backdrop};
  border-radius: 20px;
  padding: 1rem;
  width: 95%;
  max-width: 95%;
  height: 80%;
  max-height: 80%;
  color: var(--black);
  position: fixed;
  overflow-y: auto;
  overscroll-behavior: contain;
  box-shadow: 0 8px 24px ${colors.shadowHeavy};
  ${sharedScrollStyle}

  /* Always horizontally centered */
  left: 50%;
  transform: translateX(-50%);

  /* Vertical positioning */
  ${({ $top }) =>
    $top !== undefined
      ? css`
          top: ${$top}px;
        `
      : css`
          top: 50%;
          transform: translate(-50%, -50%);
        `}

  @media (max-width: 768px) {
    width: 95%;
    padding: 1.25rem;
  }
`;


export const Overlay = styled.div`
  position: fixed;
  z-index: 5;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${colors.overlay};
  backdrop-filter: blur(10px);
  display: flex;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  padding-left: var(--nav-width, 0px);
`;

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: bold;
  color: ${colors.green};
  -webkit-font-smoothing: antialiased;
`;

export const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: ${colors.green};
  cursor: pointer;
  padding: 0.25rem;

  &:hover {
    opacity: 0.8;
  }
`;

export const CarouselNavButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${({ left }) => left && 'left: 0.75rem;'}
  ${({ right }) => right && 'right: 0.75rem;'}
  background: var(--white-a25);
  backdrop-filter: blur(6px);
  color: ${colors.orange};
  font-size: 1.75rem;
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
  cursor: pointer;
  transition: all 0.25s ease;

  &:hover {
    background: var(--orange-a60);
    color: ${colors.white};
  }
`;

export const CenteredGridWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  height: 100%;
  flex: 1;
  min-height: 0;
  position: relative;
`;


export const ResponsiveCardWrapper = styled.div`
  padding: 8px;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
`;

export const PanelGrid = styled.div`
  display: flex;
  gap: 2rem;
  flex-wrap: nowrap;
  width: 100%;
`;

export const LeftColumn = styled.div`
  flex: 2;
  padding-right: 1px;
  position: sticky;
  top: 0;
  align-self: flex-start;
`;

export const RightColumn = styled.div`
  flex: 1;
  padding: 1rem;
`;

export const InfoSection = styled.div`
  background: var(--white-a40);
  border-radius: 12px;
  padding: 0.75rem 1rem;
  box-shadow: 0 1px 4px var(--shadow-a05);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

export const DropdownWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0.75rem;
`;

export const DropdownMenu = styled.div`
  position: fixed; /* portal-based */
  background: ${({ theme }) => theme.dropdownBg || 'var(--white)'};
  border-radius: 12px;
  box-shadow: 0 8px 20px var(--shadow-a08);
  padding: 0.5rem 0;
  min-width: 120px;
  z-index: 999999;

  display: flex;
  flex-direction: column;

  & > div {
    padding: 0.5rem 1rem;
    cursor: pointer;
    font-weight: 500;
    color: ${({ theme }) => theme.text || 'var(--color-text)'};
    transition: background 0.2s ease;

    &:hover {
      background: ${({ theme }) => theme.dropdownHover || 'var(--grey-100)'};
    }
  }
`;



export const DropdownLabel = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.text || 'var(--grey-700)'};
`;

export const DropdownSelect = styled.select`
  padding: 0.45rem 1.25rem;
  border: none;
  border-radius: 999px;
  background: ${({ theme, $active }) => $active ? 'var(--orange-50)' : 'var(--white-a55)'};
  color: ${({ theme }) => theme.text || 'var(--grey-700)'};
  box-shadow: 0 4px 8px var(--shadow-a08);
  font-weight: 500;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;

  appearance: none;

  &:hover {
    background: ${({ theme }) => theme.dropdownHover || 'var(--white-a80)'};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--orange-a30);
  }
`;


export const InfoLabel = styled.div`
  font-weight: 700;
  font-size: 0.85rem;
  color: var(--green-800);
`;


export const InfoValue = styled.div`
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--grey-700);
`;

export const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  background: var(--white-a15);
  border-radius: 12px;
  border: 1px solid var(--white-a20);
  box-shadow: 0 6px 18px var(--shadow-a08);
  transition: all 0.3s ease;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
`;


export const CardGrid = styled.div`
  display: grid;
  z-index: 1;
  grid-template-columns: ${({ layout }) =>
    layout === 'grid' ? 'repeat(auto-fill, minmax(200px, 1fr))' : '1fr'};
  gap: 1rem;
`;

export const CardInner = styled.div`
  display: flex;
  flex-direction: ${({ layout }) => (layout === 'list' ? 'row' : 'column')};
  align-items: center;
  gap: ${({ layout }) => (layout === 'list' ? '1rem' : '0')};
  justify-content: ${({ layout }) => (layout === 'list' ? 'flex-start' : 'center')};
  width: 100%;
`;

export const CardImageWrapper = styled.div`
  width: 100%;
  height: clamp(140px, 18vw, 220px);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.5rem;
  background: transparent;
  border-radius: 12px;
  overflow: hidden;
  will-change: auto;
  backface-visibility: hidden;

  @media (max-width: 768px) {
    height: 120px;
  }

  @media (min-width: 1920px) {
    height: 220px;
  }
`;


export const CardTitle = styled.div`
  color: var(--green-800);
  font-size: ${({ layout }) => (layout === 'list' ? '1.15rem' : '1rem')};
  font-weight: 700;
  text-align: ${({ layout }) => (layout === 'list' ? 'left' : 'center')};
  margin-top: ${({ layout }) => (layout === 'list' ? '0' : '0.5rem')};
  margin-bottom: ${({ layout }) => (layout === 'list' ? '0.25rem' : '0')};
  text-shadow: 0 1px 2px var(--shadow-a15);
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-font-smoothing: antialiased;
`;

export const CardWrapper = styled.div`
  background: var(--white-a15);
  border-radius: 16px;
  padding: 1rem;
  cursor: pointer;
  border: 1px solid var(--white-a20);
  box-shadow: 0 2px 8px var(--shadow-a05);
  display: flex;
  align-items: center;
  max-width: 100%;
  width: 100%;
  max-height: 100%;
  min-height: 250px;
  min-width: 240px;
  justify-content: ${({ layout }) => (layout === 'list' ? 'flex-start' : 'center')};
  height: ${({ layout }) => (layout === 'list' ? 'auto' : '250px')};
  transition: transform 0.2s ease, box-shadow 0.2s ease-out;
  &:hover {
    transform: scale(1.015);
    box-shadow: 0 4px 12px var(--shadow-a08);
  }
  will-change: auto;
  contain: layout paint;
  content-visibility: auto;
  backface-visibility: hidden;
`;

export const CardImageStyled = styled.img`
  width: 100%;
  height: 150px;
  object-fit: contain;
  border-radius: 8px;
`;

export const InfoGrid = styled.div`
  display: grid;
  z-index: 1;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 0.75rem;
  margin: 1.5rem 0;
`;

export const SkeletonCard = styled.div`
  position: relative;
  height: 250px;
  border-radius: 16px;
  background: var(--grey-200);
  overflow: hidden;
  backdrop-filter: blur(8px);

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: -150px;
    height: 100%;
    width: 150px;
    background: linear-gradient(
      to right,
      transparent 0%,
      var(--white-a55) 50%,
      transparent 100%
    );
    animation: shimmer 1.4s infinite;
  }

  @keyframes shimmer {
    100% {
      transform: translateX(100%);
    }
  }
`;

export const TabButton = styled.button`
  padding: 0.45rem 1.25rem;
  margin: 0 0.25rem;
  border: none;
  border-radius: 999px;
  background: ${({ $active }) => ($active ? 'var(--orange-50)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'var(--orange-500)' : 'var(--color-text)')};
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  -webkit-font-smoothing: antialiased;
  transition: all 0.2s ease;
  box-shadow: ${({ $active }) =>
    $active ? '0 4px 8px var(--shadow-a05)' : 'none'};

  // Select compatibility
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;

  &:focus {
    outline: none;
  }

  & > option {
    color: var(--color-text);
    background: var(--white);
  }
`;

export const TabContainer = styled.div`
  display: flex;
  gap: 0.5rem;
`;

export const ToggleIcon = styled.button`
  background: transparent;
  border: 1px solid transparent;
  border-radius: 10px;
  padding: 0.4rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--black);
  cursor: pointer;
  transition: all 0.25s ease;

  ${({ $active }) =>
    $active &&
    `
    color: var(--orange-500);
    box-shadow: 0 0 6px 1px var(--orange-a60);
  `}

  &:hover {
    background: var(--white-a20);
    color: var(--orange-500);
    border: 1px solid var(--orange-a30);
  }
`;

export const ToggleWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
`;

export const CardImage = styled.img`
  max-height: 100%;
  max-width: 100%;
  border-radius: 10px;
  object-fit: contain;
  transition: none;
  will-change: auto;
  contain: layout paint;

  @media (max-width: 768px) {
    max-height: 90%;
  }

  @media (min-width: 1920px) {
    max-height: 95%;
  }
`;

export const Label = styled.div`
  font-size: 0.95rem;
  margin-top: 1rem;
  margin-bottom: 0.25rem;
  font-weight: 700;
  color: var(--green-800);
  -webkit-font-smoothing: antialiased;
`;


export const StyledInput = styled.input`
  width: 100%;
  padding: 0.5rem 0.75rem;
  border-radius: 10px;
  background: var(--white-a15);
  backdrop-filter: blur(10px);
  border: 1px solid var(--white-a20);
  color: var(--shadow-a80);
  font-size: 0.9rem;
  outline: none;

  &::placeholder {
    color: var(--shadow-a45);
  }

  &:focus {
    border: 2px solid var(--orange-500);
    box-shadow: 0 0 0 1px var(--orange-a12);
  }
`;

export const StyledSelect = styled.select`
  width: 100%;
  padding: 0.5rem 2.5rem 0.5rem 0.75rem;
  border-radius: 10px;
  border: 1px solid var(--white-a20);
  background: var(--white-a15);
  backdrop-filter: blur(10px);
  color: var(--shadow-a80);
  font-size: 0.9rem;
  outline: none;
  appearance: none;
  cursor: pointer;
  transition: all 0.2s ease;
  background-image: url("data:image/svg+xml,%3Csvg fill='%23000000cc' height='20' viewBox='0 0 24 24' width='20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 1rem;

  &:focus {
    border: 1px solid var(--orange-500);
    box-shadow: 0 0 0 2px var(--orange-a30);
  }

  option {
    background: var(--cream-50);
    color: var(--shadow-a80);
  }
`;

export const Content = styled.div`
  margin-top: 1rem;
`;

export const CarouselWrapper = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
`;

export const ImageSlide = styled.img`
  display: block;
  margin: 0 auto;
  max-width: 100%;
  object-fit: contain;

`;

export const SectionDivider = styled.div`
  width: 1px;
  background: var(--shadow-a08);
`;

export const BarcodeWrapper = styled.div`
  background: var(--white-a40);
  padding: 0.75rem 1rem;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-shadow: 0 2px 6px var(--shadow-a05);
`;

export const GroupWrapper = styled.div`
  margin-top: 1rem;
`;

export const GroupTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  margin-top: 0.75rem;
  margin-bottom: 0.25rem;
  color: var(--black);
`;

export const GroupSub = styled.div`
  font-size: 0.95rem;
  font-weight: 500;
  margin-left: 1rem;
  margin-bottom: 0.25rem;
  color: var(--grey-700);
`;

export const SizeTagWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  align-items: center;
  justify-content: center;
  padding-bottom: 0.5rem;
`;

export const SizeTag = styled.div`
  background: var(--shadow-a05);
  border-radius: 12px;
  padding: 0.3rem 0.75rem;
  font-size: 0.85rem;
  color: var(--black);
  font-weight: 500;
`;

export const StyleGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
`;

export const StyleCard = styled.div`
  background: var(--white-a40);
  border-radius: 12px;
  padding: 0.5rem;
  min-width: auto;
  box-shadow: 0 2px 6px var(--shadow-a05);

  h4 {
    font-size: 0.8rem;
    font-weight: 400;
    color: var(--black);
    width: max-content;
  }
`;

export const Spinner = styled.div`
  margin: 2rem auto;
  border: 4px solid var(--danger-a15);
  border-top: 4px solid var(--black);
  border-radius: 50%;
  width: 30px;
  height: 30px;
  animation: spin 0.7s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const NoResultsMessage = styled.div`
  font-size: 1rem;
  color: var(--grey-500);
  margin: 2rem auto;
  text-align: center;
`;

export const NoImageBadge = styled.span`
  position: absolute;
  top: 8px;
  right: 8px;
  background-color: var(--shadow-a65);
  color: var(--white);
  font-size: 0.65rem;
  padding: 2px 6px;
  border-radius: 6px;
  font-weight: bold;
  letter-spacing: 0.5px;
  pointer-events: none;
`;

export const LargeImage = styled.img`
  width: 100%;
  height: auto;
  object-fit: contain;
  background-color: var(--grey-100);
  border-radius: 12px;
`;

export const TopSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const DetailGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 1rem;
  align-items: stretch; /* same height per row */
`;

export const InfoRow = styled.div`
  background: var(--white-a40);
  padding: 1rem;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 0.3rem;
  box-shadow: 0 2px 6px var(--shadow-a05);
  white-space: normal;
  flex: 1 1 auto;         
  min-width: 180px;
`;

export const InfoRowSplit = styled.div`
  background: var(--white-a40);
  padding: 1em 1.25rem;
  border-radius: 12px;
  display: flex;
  flex: 1 1 auto;
  min-width: 250px;
  align-items: center;
  box-shadow: 0 2px 6px var(--shadow-a05);
  gap: 1rem;
  white-space: normal;

  .column {
    display: flex;
    flex-direction: column;
  }

  .vertical-divider {
    width: 1px;
    background: var(--shadow-a08);
    align-self: stretch;
  }

  @media (max-width: 768px) {
    flex-direction: column;

    .vertical-divider {
      display: none;
    }

    .left,
    .right {
      width: 100%;
    }
  }
`;



export const HighlightedTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--green-800);
`;

/* ---------- PID Displays: panel helpers ---------- */
export const PIDDisPanel = styled(Panel)`
  /* Header stays visible; body is the only scroller */
  display: flex;
  flex-direction: column;
  overflow: hidden;
  /* Enables stacking based on modal width (container queries) */
  container-type: inline-size;
`;

export const PIDDisHeader = styled(Header)`
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 0;
`;

export const PIDDisTitle = styled(Title)`
  font-size: clamp(1.2rem, 1.1vw + 1rem, 1.85rem);
  line-height: 1.15;
  margin: 0;
  padding: 0.15rem 0;
  white-space: normal;
  overflow-wrap: anywhere;
`;

export const PIDDisBodyScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  overscroll-behavior: contain;
  padding: 0.75rem 0.25rem 0.25rem;
  ${sharedScrollStyle}
`;

export const PIDDisLayout = styled.div`
  /* Use container queries when supported (stack based on modal width) */
  display: grid;
  grid-template-columns: minmax(320px, 1.2fr) 1px minmax(360px, 1fr);
  gap: 1.25rem;
  align-items: start;

  @container (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  /* Fallback when container queries aren’t supported */
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

export const PIDDisMediaColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 0;
`;

export const PIDDisDivider = styled.div`
  width: 1px;
  background: var(--shadow-a08);

  @container (max-width: 900px) {
    display: none;
  }

  @media (max-width: 900px) {
    display: none;
  }
`;

export const PIDDisDetailsColumn = styled.div`
  min-width: 0;
  padding: 0.25rem 0.5rem 0.5rem;

  @container (max-width: 900px) {
    padding: 0.25rem 0;
  }

  @media (max-width: 900px) {
    padding: 0.25rem 0;
  }
`;

export const PIDDisDetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.6rem;
  margin-top: 1rem;
  padding: 1rem;
  align-items: stretch;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const PIDDisImageBox = styled.div`
  width: 100%;
  height: clamp(300px, 65vh, 900px);
  min-height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: clamp(8px, 1.1vw, 16px);

  & > img,
  & > div {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 12px;
  }

  & > img {
    cursor: zoom-in;
  }

  @media (max-width: 768px) {
    height: clamp(300px, 65vh, 900px);
  }
`;

export const LightboxBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 3000;
  background: var(--shadow-a80);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

export const LightboxImage = styled.img`
  max-width: 92vw;
  max-height: 92vh;
  object-fit: contain;
  border-radius: 12px;
  box-shadow: 0 18px 60px var(--shadow-a35);
`;

export const LightboxCloseButton = styled.button`
  position: fixed;
  top: 14px;
  right: 18px;
  width: 38px;
  height: 38px;
  border-radius: 19px;
  background: var(--white-a90);
  border: 1px solid var(--shadow-a15);
  cursor: pointer;
  font-size: 20px;
  line-height: 1;
  font-weight: 800;
  color: var(--color-text);

  &:hover {
    opacity: 0.92;
  }
`;

export const TwoColumnSection = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

export const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
`;

export const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    background: 'var(--white-a15)',
    backdropFilter: 'blur(10px)',
    border: state.isFocused
      ? '2px solid var(--orange-500)'
      : '1px solid var(--white-a25)',
    borderRadius: '10px',
    boxShadow: state.isFocused
      ? '0 0 0 2px var(--orange-a12)'
      : 'none',
    minHeight: '38px',
    fontWeight: 500,
    transition: 'all 0.2s ease-in-out',
    cursor: 'pointer',
    '&:hover': {
      borderColor: 'var(--orange-500)',
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0 8px',
  }),
  singleValue: (base) => ({
    ...base,
    color: 'var(--color-text)',
    fontWeight: 600,
  }),
  placeholder: (base) => ({
    ...base,
    color: 'var(--grey-600)',
    fontStyle: 'italic',
  }),
  dropdownIndicator: (base, state) => ({
    ...base,
    color: state.isFocused ? 'var(--orange-500)' : 'var(--grey-500)',
    transition: 'color 0.2s ease',
    '&:hover': { color: 'var(--orange-500)' },
  }),
  clearIndicator: (base) => ({
    ...base,
    color: 'var(--grey-400)',
    '&:hover': { color: 'var(--orange-500)' },
  }),
  menu: (base) => ({
    ...base,
    background: 'var(--color-surface)',
    borderRadius: '10px',
    padding: '4px 0',
    boxShadow: '0 8px 24px var(--shadow-a15)',
    backdropFilter: 'blur(6px)',
    zIndex: 9999,
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? 'var(--orange-a12)'
      : state.isFocused
      ? 'var(--shadow-a05)'
      : 'transparent',
    color: 'var(--color-text)',
    fontWeight: state.isSelected ? 600 : 500,
    padding: '10px 14px',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  }),
  input: (base) => ({
    ...base,
    color: 'var(--color-text)',
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

